import { ResizeSensor2 } from '@blueprintjs/popover2';
import { AddLarge, ChevronDown, ChevronUp, Renew } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import { MosaicKey } from 'react-mosaic-component/lib/types';
import { useDebouncedCallback } from 'use-debounce';
import {
  Body,
  Button,
  OutlinedIconButton,
  StaticBadge,
  Tooltip,
} from 'vet-bones/design-system/components';

import {
  TEST_IDS,
  getProjectSidebarSectionTestId,
} from 'src/constants/testIds';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { MosaicComponentProps } from 'src/utils/mosaic';

import styles from 'src/components/sidebarSection/sidebarSection.module.scss';

export type SidebarSectionType = {
  id: string;
  addDisabled?: boolean;
  addDisabledTooltip?: string;
  allCount: number;
  items: React.ReactNode;
  itemsClassname?: string;
  itemsCount: number;
  refreshDisabled?: boolean;
  refreshTooltip?: string;
  title: string;
  onAdd?: () => void;
  onRefresh?: () => void;
};

export type SidebarSectionProps<T extends MosaicKey> =
  MosaicComponentProps<T> & {
    filter: string;
    section: SidebarSectionType;
  };

export const SidebarSection: React.VFC<SidebarSectionProps<any>> = <
  T extends MosaicKey
>({
  filter,
  section,
  collapsed,
  onToggleCollapsed,
}: SidebarSectionProps<T>) => {
  const isMounted = useIsMounted();

  let count = '';
  if (filter.trim()) {
    count = `${section.itemsCount.toLocaleString()} of ${section.allCount.toLocaleString()}`;
  } else if (collapsed) {
    count = section.allCount.toLocaleString();
  }

  const [scrollKey, setScrollKey] = React.useState('');
  const onResize = React.useCallback(
    (entries: ResizeObserverEntry[]) => {
      if (isMounted.current) {
        const clientHeight = entries[0]?.target.clientHeight ?? 0;
        const scrollHeight = entries[0]?.target.scrollHeight ?? 0;
        setScrollKey(`${clientHeight < scrollHeight}`);
      }
    },
    [isMounted]
  );
  const debouncedOnResize = useDebouncedCallback(onResize, 500);

  return (
    <div
      className={styles.sidebarSection}
      data-testid={getProjectSidebarSectionTestId(section.id)}
      id={section.id}
    >
      <div className={styles.sidebarSectionHeader}>
        <div className={styles.sidebarSectionHeaderTitle}>
          <OutlinedIconButton
            elevated
            leftIcon={collapsed ? ChevronDown : ChevronUp}
            onClick={onToggleCollapsed}
            testId={TEST_IDS.projectSidebar.sectionToggleButton}
          />
          <Body size="lg">{section.title}</Body>
          {count ? (
            <StaticBadge size="md" type="default" variant="muted">
              {count}
            </StaticBadge>
          ) : null}
          {section.onRefresh ? (
            <Tooltip
              description={section.refreshTooltip || ''}
              disabled={section.refreshDisabled}
            >
              <Button
                disabled={section.refreshDisabled}
                leftIcon={Renew}
                onClick={() => {
                  if (collapsed) {
                    onToggleCollapsed?.();
                  }
                  section.onRefresh?.();
                }}
                size="xs"
                testId={TEST_IDS.projectSidebar.refreshButton}
                variant="ghost"
              />
            </Tooltip>
          ) : null}
        </div>
        <div className={styles.sidebarSectionHeaderActions}>
          {section.onAdd ? (
            <Tooltip
              description={section.addDisabledTooltip || ''}
              disabled={!section.addDisabled}
            >
              <Button
                disabled={section.addDisabled}
                leftIcon={AddLarge}
                onClick={() => {
                  if (collapsed) {
                    onToggleCollapsed?.();
                  }
                  section.onAdd?.();
                }}
                size="xs"
                testId={TEST_IDS.projectSidebar.addButton}
                variant="secondary-solid-gray"
              />
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className={styles.sidebarSectionScroll}>
        <ResizeSensor2 onResize={debouncedOnResize}>
          <div
            key={scrollKey}
            className={classNames(
              styles.sidebarSectionItems,
              section.itemsClassname
            )}
          >
            {collapsed ? null : section.items}
          </div>
        </ResizeSensor2>
      </div>
    </div>
  );
};
